import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const ACCOUNT_STYLE_ID = 'oasis-account-shadow-styles';
const LOGIN_FORM_STYLE_ID = 'oasis-account-login-shadow-styles';

const ACCOUNT_SHADOW_CSS = `
  :host {
    --app-width: min(24rem, calc(100vw - 2rem));
    --app-page-spacing: clamp(0.9rem, 2vw, 1.5rem);
    --padding-base: 0.95rem;
    --spacing-xs: 0.2rem;
    --spacing-sm: 0.55rem;
    --spacing-md: 0.8rem;
    --spacing-lg: 1rem;
    --spacing-header-gap: 0.35rem;
    --size-icon-md: 1rem;
    --size-close-background: 1.8rem;
    --color-backdrop: rgb(15 23 42 / 0.3);
    --color-shadow:
      0 22px 54px rgb(15 23 42 / 0.26),
      0 6px 18px rgb(15 23 42 / 0.12);
    --shadow-control: none;
  }

  .dialog {
    border: 1px solid rgb(247 249 251 / 0.08);
    box-shadow: var(--color-shadow);
  }

  .dialog::backdrop {
    background: rgb(15 23 42 / 0.24);
    backdrop-filter: blur(4px);
  }

  .account {
    max-height: min(39rem, calc(100dvh - var(--shopify-account-dialog-position-top) - 1.5rem));
    scrollbar-width: thin;
    scrollbar-color: rgb(247 249 251 / 0.28) transparent;
  }

  .account__header {
    gap: 0.75rem;
    padding: 1rem 1rem 0.5rem;
    background:
      linear-gradient(180deg, rgb(44 62 80 / 0.98) 0%, rgb(44 62 80 / 0.94) 82%, rgb(44 62 80 / 0) 100%);
  }

  .account__header-content {
    gap: 0.2rem;
    padding-inline: 0;
  }

  .account__heading {
    min-height: 1.8rem;
    font-size: 1.22rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .account__email {
    font-size: 0.92rem;
  }

  .account__close,
  .account__back-button {
    width: 1.8rem;
    height: 1.8rem;
    color: rgb(247 249 251 / 0.88);
    background: rgb(247 249 251 / 0.08);
    border: 1px solid rgb(247 249 251 / 0.12);
  }

  .account__close:hover,
  .account__back-button:hover {
    background: rgb(247 249 251 / 0.12);
  }

  .account__content {
    gap: 0.8rem;
    padding: 0 1rem 1rem;
    margin-top: 0;
    padding-top: 0.15rem;
  }

  .account__login-form-container:empty {
    display: none;
  }

  .account__shortcuts {
    border-color: rgb(247 249 251 / 0.12);
    background:
      linear-gradient(180deg, rgb(247 249 251 / 0.08), rgb(247 249 251 / 0.05));
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.04);
  }

  .account__item-wrapper {
    --edge-padding: 0.95rem;
  }

  .account__item-wrapper:before {
    background-color: rgb(247 249 251 / 0.1);
  }

  .account__item {
    min-height: 2.85rem;
    padding-block: 0.8rem;
    font-size: 0.94rem;
    color: rgb(247 249 251 / 0.78);
  }

  .account__item:hover,
  .account__item:focus-visible {
    background: rgb(247 249 251 / 0.08);
    color: #ffffff;
  }

  .account__item-text {
    font-weight: 500;
  }

  .account__item-arrow {
    color: rgb(247 249 251 / 0.46);
  }

  @media (min-width: 751px) {
    .dialog {
      inset-block-start: calc(var(--shopify-account-dialog-position-top) + 0.75rem);
      inset-inline-end: clamp(1rem, 3vw, 2rem);
      width: min(24rem, calc(100vw - 2rem));
      max-height: min(39rem, calc(100dvh - var(--shopify-account-dialog-position-top) - 1.5rem));
    }
  }

  @media (max-width: 750px) {
    :host {
      --app-page-spacing: 0;
      --padding-base: 0.9rem;
      --spacing-sm: 0.5rem;
      --spacing-md: 0.72rem;
      --spacing-lg: 0.9rem;
      --size-close-background: 1.7rem;
    }

    .dialog {
      border: 1px solid rgb(247 249 251 / 0.08);
      border-bottom: 0;
      border-top-left-radius: 1.5rem;
      border-top-right-radius: 1.5rem;
      min-height: 0;
      max-height: min(72dvh, 34rem);
      box-shadow: 0 -14px 36px rgb(15 23 42 / 0.26);
    }

    .account {
      max-height: min(72dvh, 34rem);
    }

    .account__header {
      padding: 0.9rem 0.9rem 0.45rem;
    }

    .account__content {
      gap: 0.72rem;
      padding: 0 0.9rem calc(0.9rem + env(safe-area-inset-bottom));
    }

    .account__item {
      min-height: 2.65rem;
      padding-block: 0.72rem;
    }
  }
`;

const LOGIN_FORM_SHADOW_CSS = `
  .account__login-form {
    gap: 0.78rem;
  }

  .account__show-logins-button,
  .account__use-email-button {
    min-height: 2.6rem;
    justify-content: center;
    padding: 0.72rem 0.9rem;
    border: 1px solid rgb(247 249 251 / 0.12);
    background: rgb(247 249 251 / 0.08);
    color: rgb(247 249 251 / 0.78);
    font-size: 0.8rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    box-shadow: none;
  }

  .account__show-logins-button:hover,
  .account__use-email-button:hover {
    background: rgb(247 249 251 / 0.12);
    color: #ffffff;
  }

  .account__social-login-button {
    min-height: 3.1rem;
    padding: 0.9rem 1rem;
    border-radius: 999px;
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 0.08),
      0 12px 24px rgb(15 23 42 / 0.18);
    font-size: 0.96rem;
    font-weight: 600;
  }

  .account__social-login-button.shop-login {
    border: 1px solid rgb(255 255 255 / 0.08);
    background: linear-gradient(180deg, #35495d 0%, #233241 100%);
  }

  .account__social-login-button.shop-login:hover {
    background: linear-gradient(180deg, #3c5268 0%, #27394a 100%);
  }

  .account__divider {
    gap: 0.7rem;
    margin-block: 0.05rem;
  }

  .account__divider-line {
    height: 1px;
    background: rgb(247 249 251 / 0.14);
  }

  .account__divider-text {
    color: rgb(247 249 251 / 0.54);
    letter-spacing: 0.16em;
  }

  .account__email-form {
    gap: 0.45rem;
  }

  .account__email-input-wrapper {
    --input-height: 3.25rem;
    --submit-button-size: 2.2rem;
    --submit-button-gutter: 0.52rem;
  }

  .account__email-input {
    padding-inline: 1rem 3.85rem;
    border: 1px solid rgb(35 50 65 / 0.16);
    border-radius: 1rem;
    color: #233241;
    background: #f8fafc;
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 0.72),
      0 10px 20px rgb(15 23 42 / 0.08);
  }

  .account__email-input:hover {
    border-color: rgb(35 50 65 / 0.26);
  }

  .account__email-input:focus-visible {
    border-color: #2c3e50;
    box-shadow:
      inset 0 1px 0 rgb(255 255 255 / 0.72),
      0 0 0 3px rgb(242 237 230 / 0.32);
  }

  .account__email-input::placeholder {
    color: rgb(35 50 65 / 0.46);
  }

  .account__email-label {
    color: rgb(35 50 65 / 0.6);
  }

  .account__email-submit {
    width: 2.2rem;
    height: 2.2rem;
    border: 1px solid rgb(35 50 65 / 0.08);
    background: linear-gradient(180deg, #2c3e50 0%, #233241 100%);
    color: #f8fafc;
    box-shadow: 0 8px 18px rgb(15 23 42 / 0.18);
  }

  .account__email-submit:hover:not(:disabled) {
    background: linear-gradient(180deg, #35495d 0%, #27394a 100%);
  }

  .account__email-submit:disabled {
    border-color: rgb(35 50 65 / 0.08);
    background: rgb(35 50 65 / 0.08);
    color: rgb(35 50 65 / 0.46);
    box-shadow: none;
  }

  .stack-inline {
    gap: 0.6rem;
  }

  .stack-inline .account__social-login-button {
    min-height: 2.75rem;
    border-radius: 999px;
    box-shadow: none;
    border-color: rgb(247 249 251 / 0.14);
    background: rgb(247 249 251 / 0.06);
    color: rgb(247 249 251 / 0.8);
  }

  .stack-inline .account__social-login-button:hover {
    background: rgb(247 249 251 / 0.1);
    color: #ffffff;
  }

  @media (max-width: 750px) {
    .account__login-form {
      gap: 0.72rem;
    }

    .account__social-login-button {
      min-height: 3rem;
      padding-inline: 0.95rem;
    }

    .account__email-input-wrapper {
      --input-height: 3.15rem;
      --submit-button-size: 2.1rem;
      --submit-button-gutter: 0.52rem;
    }

    .account__show-logins-button,
    .account__use-email-button {
      min-height: 2.45rem;
      font-size: 0.76rem;
    }
  }
`;

/**
 * Inject a style tag into an open shadow root exactly once.
 * @param {ShadowRoot | null} shadowRoot
 * @param {string} id
 * @param {string} cssText
 */
function injectShadowStyles(shadowRoot, id, cssText) {
  if (!shadowRoot || shadowRoot.getElementById(id)) return;

  const style = document.createElement('style');
  style.id = id;
  style.textContent = cssText;
  shadowRoot.append(style);
}

/**
 * Header actions component that manages cart notifications and account UI polish.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];
  #accountObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('shopify-render', this.#onShopifyRender);
    this.#observeAccountComponents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('shopify-render', this.#onShopifyRender);
    this.#accountObserver?.disconnect();
    this.#accountObserver = null;
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  #onShopifyRender = () => {
    this.#applyAccountEnhancements();
  };

  #observeAccountComponents() {
    customElements.whenDefined('shopify-account').then(() => {
      this.#applyAccountEnhancements();

      if (this.#accountObserver) return;

      this.#accountObserver = new MutationObserver(() => {
        this.#applyAccountEnhancements();
      });

      this.#accountObserver.observe(this, {
        childList: true,
        subtree: true,
      });
    });
  }

  #applyAccountEnhancements() {
    const accounts = this.querySelectorAll('shopify-account');

    accounts.forEach((account) => {
      injectShadowStyles(account.shadowRoot, ACCOUNT_STYLE_ID, ACCOUNT_SHADOW_CSS);
      this.#enhanceLoginForms(account);
    });
  }

  /**
   * @param {Element} account
   */
  #enhanceLoginForms(account) {
    const loginForms = account.shadowRoot?.querySelectorAll('shopify-login-form') ?? [];

    loginForms.forEach((loginForm) => {
      injectShadowStyles(loginForm.shadowRoot, LOGIN_FORM_STYLE_ID, LOGIN_FORM_SHADOW_CSS);
      this.#forceCompactShopButton(loginForm);

      if (loginForm.dataset.oasisObserved === 'true') return;

      const observer = new MutationObserver(() => {
        this.#forceCompactShopButton(loginForm);
      });

      observer.observe(loginForm, {
        childList: true,
        subtree: true,
      });

      loginForm.dataset.oasisObserved = 'true';
    });
  }

  /**
   * Replace the bulky Shop card treatment with the compact button presentation.
   * @param {Element} loginForm
   */
  #forceCompactShopButton(loginForm) {
    const shopLogin = loginForm.querySelector('shop-login');
    if (!shopLogin) return;

    if (shopLogin.getAttribute('presentation-mode') !== 'button') {
      shopLogin.setAttribute('presentation-mode', 'button');
    }
  }
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
